import { useState, useEffect, useCallback } from 'react';

export interface FeatureWizardConfig {
  id: string;
  title: string;
  description: string;
  showOnFirstVisit?: boolean;
  showOnRoute?: string; // Show when user visits specific route
  showOnAction?: string; // Show when user performs specific action
  autoShow?: boolean; // Show automatically without user interaction
}

export interface FeatureWizardState {
  [featureId: string]: {
    hasSeen: boolean;
    lastSeen?: string; // Store as ISO string for localStorage compatibility
    completedSteps: number[];
  };
}

const STORAGE_KEY = 'featureWizards';

export const useFeatureWizard = (config: FeatureWizardConfig) => {
  const [isOpen, setIsOpen] = useState(false);
  const [wizardState, setWizardState] = useState<FeatureWizardState>({});

  // Load wizard state from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setWizardState(JSON.parse(stored));
      } catch (error) {
        console.error('Error parsing feature wizard state:', error);
      }
    }
  }, []);

  // Save wizard state to localStorage
  const saveWizardState = useCallback((newState: FeatureWizardState) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    setWizardState(newState);
  }, []);

  // Check if user has seen this feature
  const hasSeenFeature = useCallback(() => {
    return wizardState[config.id]?.hasSeen || false;
  }, [wizardState, config.id]);

  // Mark feature as seen
  const markAsSeen = useCallback(() => {
    const newState: FeatureWizardState = {
      ...wizardState,
      [config.id]: {
        hasSeen: true,
        lastSeen: new Date().toISOString(),
        completedSteps: wizardState[config.id]?.completedSteps || [],
      },
    };
    saveWizardState(newState);
  }, [wizardState, config.id, saveWizardState]);

  // Mark step as completed
  const markStepCompleted = useCallback(
    (stepIndex: number) => {
      const currentSteps = wizardState[config.id]?.completedSteps || [];
      const newSteps = Array.from(new Set([...currentSteps, stepIndex]));

      const newState: FeatureWizardState = {
        ...wizardState,
        [config.id]: {
          hasSeen: true,
          lastSeen: new Date().toISOString(),
          completedSteps: newSteps,
        },
      };
      saveWizardState(newState);
    },
    [wizardState, config.id, saveWizardState]
  );

  // Open wizard
  const openWizard = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close wizard
  const closeWizard = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Complete wizard
  const completeWizard = useCallback(() => {
    markAsSeen();
    setIsOpen(false);
  }, [markAsSeen]);

  // Skip wizard
  const skipWizard = useCallback(() => {
    markAsSeen();
    setIsOpen(false);
  }, [markAsSeen]);

  // Auto-show logic
  useEffect(() => {
    if (config.autoShow && !hasSeenFeature()) {
      // Add a small delay to ensure the page is fully loaded
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [config.autoShow, hasSeenFeature, wizardState]);

  // Route-based auto-show
  useEffect(() => {
    if (config.showOnRoute && !hasSeenFeature()) {
      const currentPath = window.location.pathname;
      if (currentPath.includes(config.showOnRoute)) {
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 1500);

        return () => clearTimeout(timer);
      }
    }
  }, [config.showOnRoute, hasSeenFeature, wizardState]);

  return {
    isOpen,
    openWizard,
    closeWizard,
    completeWizard,
    skipWizard,
    hasSeenFeature: hasSeenFeature(),
    markStepCompleted,
    getCompletedSteps: () => wizardState[config.id]?.completedSteps || [],
  };
};

// Hook to manage multiple feature wizards
export const useFeatureWizards = () => {
  const [wizards, setWizards] = useState<FeatureWizardState>({});

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setWizards(JSON.parse(stored));
      } catch (error) {
        console.error('Error parsing feature wizards state:', error);
      }
    }
  }, []);

  const resetWizard = useCallback(
    (featureId: string) => {
      const newWizards = { ...wizards };
      delete newWizards[featureId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newWizards));
      setWizards(newWizards);
    },
    [wizards]
  );

  const resetAllWizards = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setWizards({});
  }, []);

  const getWizardState = useCallback(
    (featureId: string) => {
      return wizards[featureId] || { hasSeen: false, completedSteps: [] };
    },
    [wizards]
  );

  return {
    wizards,
    resetWizard,
    resetAllWizards,
    getWizardState,
  };
};
